import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { BdcDispatchNote } from "../../../types/bdc.type";
import { pcs, thaiDate, thaiDateTime } from "../format";
import { CARD_SX } from "../styles";
import Tag from "../Tag";
import { bdcNewCreditNoteLink } from "../constants";
import {
  DISPATCH_NAME_MAX_LENGTH,
  FOOTER_FIELDS,
  FooterField,
  LineField,
  RecordDraft,
  RecordErrors,
  deliveredOf,
  footerText,
  hasShort,
  lineText,
  previewTotals,
} from "./record";

const ERROR = "#C62828";

/** ขาด / เหลือ of one line: a narrow right-aligned box, red with a tooltip when wrong. */
function LineInput({
  value,
  label,
  error,
  onChange,
}: Readonly<{ value: string; label: string; error?: string; onChange: (text: string) => void }>) {
  return (
    <Tooltip title={error ?? ""} arrow>
      <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        inputProps={{ "aria-label": label, "aria-invalid": Boolean(error), inputMode: "numeric" }}
        sx={{
          width: 76,
          fontSize: "0.8125rem",
          border: `1px solid ${error ? ERROR : "#C9CBD2"}`,
          borderRadius: "5px",
          bgcolor: "#FFFFFF",
          "& input": { padding: "3px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" },
          "& input::placeholder": { color: "#9A9EA8", opacity: 1 },
          "&.Mui-focused": { borderColor: error ? ERROR : "#363A44" },
        }}
      />
    </Tooltip>
  );
}

const shown = (text: string) => (text === "" ? "-" : text);

/**
 * บันทึกใบจ่ายกลับ — laid out as the paper is filled: the foot (คนขับ, ผู้จ่าย, ถาด, CN, เวลากลับ,
 * เลขไมล์), then ขาด / เหลือ per line with เบิก / ถาด / ชิ้น read-only and ส่งถึง WB computed.
 * Read-only for head office. A short DC links to a new CN draft (pick that DC's INV there).
 */
export default function RecordPanel({
  note,
  draft,
  errors,
  editable,
  dirty,
  saving,
  onFooterChange,
  onLineChange,
  onSave,
  onDiscard,
}: Readonly<{
  note: BdcDispatchNote;
  draft: RecordDraft;
  errors: RecordErrors;
  editable: boolean;
  dirty: boolean;
  saving: boolean;
  onFooterChange: (field: FooterField, text: string) => void;
  onLineChange: (barcode: string, field: LineField, text: string) => void;
  onSave: () => void;
  onDiscard: () => void;
}>) {
  const totals = previewTotals(note, draft);
  const short = hasShort(note, draft);
  // a printed note may be saved untouched (nothing came back short) — that marks it recorded
  const canSave = editable && errors.count === 0 && (dirty || note.status === "printed");

  return (
    <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }} data-testid="bdc-dispatch-record">
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Box>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
            บันทึกใบจ่ายกลับ · {note.dc_code} {note.dc_name} · {thaiDate(note.work_date)}
          </Typography>
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
            {note.status === "recorded" ? <Tag tone="ok">บันทึกแล้ว</Tag> : <Tag tone="info">พิมพ์แล้ว</Tag>}
            <span className="sub">
              พิมพ์ {note.print_count} ครั้ง · ล่าสุด {thaiDateTime(note.printed_at)}
              {note.printed_by_name ? ` โดย ${note.printed_by_name}` : ""}
              {note.recorded_at
                ? ` · บันทึก ${thaiDateTime(note.recorded_at)}${note.recorded_by_name ? ` โดย ${note.recorded_by_name}` : ""}`
                : ""}
            </span>
          </Stack>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {short && editable && (
            <Button variant="outlined" component={RouterLink} to={bdcNewCreditNoteLink()}>
              สร้าง CN ของ {note.dc_code} →
            </Button>
          )}
          {editable && dirty && (
            <Button variant="text" disabled={saving} onClick={onDiscard}>
              ยกเลิกการแก้ไข
            </Button>
          )}
          {editable && (
            <Button variant="contained" disabled={!canSave} loading={saving} onClick={onSave}>
              บันทึก
            </Button>
          )}
        </Stack>
      </Stack>

      {note.is_changed && (
        <Alert severity="warning">
          ยอดเบิกของ DC นี้เปลี่ยนหลังพิมพ์ (นำเข้าไฟล์ใหม่) — ตัวเลขด้านล่างเป็นยอดตามใบที่พิมพ์
          {note.status === "printed" ? " · พิมพ์ซ้ำเพื่อให้ใบตรงกับยอดล่าสุด" : ""}
        </Alert>
      )}
      {editable && errors.count > 0 && (
        <Alert severity="error">มีช่องที่ต้องแก้ {errors.count} ช่อง — วางเมาส์บนช่องสีแดงเพื่อดูเหตุผล</Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 1.25,
        }}
      >
        {FOOTER_FIELDS.map(({ key, label, placeholder }) => {
          const text = footerText(note, draft, key);
          if (!editable) {
            return (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2">{shown(text)}</Typography>
              </Box>
            );
          }
          const error = errors.footer[key];
          const isName = key === "driver_name" || key === "issuer_name" || key === "recorder_name";
          return (
            <TextField
              key={key}
              label={label}
              size="small"
              value={text}
              placeholder={placeholder}
              error={Boolean(error)}
              helperText={error}
              onChange={(e) => onFooterChange(key, e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  maxLength: isName ? DISPATCH_NAME_MAX_LENGTH : 12,
                  inputMode: isName ? "text" : "numeric",
                },
              }}
            />
          );
        })}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <table className="bdc-t" data-testid="bdc-dispatch-lines">
          <thead>
            <tr>
              <th>สินค้า</th>
              <th className="n">เบิก</th>
              <th className="n">ถาด</th>
              <th className="n">ชิ้น</th>
              <th className="n">ขาด</th>
              <th className="n">เหลือ</th>
              <th className="n">ส่งถึง {note.dc_code}</th>
            </tr>
          </thead>
          <tbody>
            {note.lines.map((line) => {
              const lineErrors = errors.lines[line.barcode] ?? {};
              const sumError = lineErrors.sum;
              return (
                <tr key={line.barcode}>
                  <td>
                    {line.name}
                    <div className="sub mono">
                      {line.item_code} · {line.pcs_per_tray} ชิ้น/ถาด
                    </div>
                  </td>
                  <td className="n">{pcs(line.issued_qty)}</td>
                  <td className="n">{pcs(line.trays)}</td>
                  <td className="n">{pcs(line.pieces)}</td>
                  {(["short_qty", "leftover_qty"] as const).map((field) => (
                    <td key={field} className="n">
                      {editable ? (
                        <LineInput
                          value={lineText(line, draft, field)}
                          label={`${field === "short_qty" ? "ขาด" : "เหลือ"} ${line.name}`}
                          error={lineErrors[field] ?? sumError}
                          onChange={(text) => onLineChange(line.barcode, field, text)}
                        />
                      ) : (
                        shown(lineText(line, draft, field))
                      )}
                    </td>
                  ))}
                  <td className="n" style={sumError ? { color: ERROR } : undefined}>
                    {pcs(deliveredOf(line, draft))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>ยอดรวม {note.lines.length} รายการ</td>
              <td className="n">{pcs(totals.issued_qty)}</td>
              <td className="n">{pcs(totals.trays)}</td>
              <td className="n">{pcs(totals.pieces)}</td>
              <td className="n">{pcs(totals.short_qty)}</td>
              <td className="n">{pcs(totals.leftover_qty)}</td>
              <td className="n">{pcs(totals.delivered_qty)}</td>
            </tr>
          </tfoot>
        </table>
      </Box>
      <Typography variant="caption" color="text.secondary">
        ขาด = ของที่ WB นับได้น้อยกว่าที่เบิก · เหลือ = ของที่พาไปแล้วนำกลับมา (ความหมายรอยืนยัน แผนข้อ 9.3) · ขาด + เหลือ
        ต้องไม่เกินเบิก · ช่องว่าง = ไม่มี · บันทึกแล้วใบจะเป็น "บันทึกแล้ว"
        {short ? " · มีขาด — สร้าง CN จากปุ่มด้านบน (เลือก INV ของ DC นี้)" : ""}
      </Typography>
    </Card>
  );
}
